import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, switchMap, of, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface User {
  id: string;
  _id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  role: string;
  permissions: string[];
  is_active?: boolean;
  is_verified?: boolean;
  has_2fa_enabled?: boolean;
  organization_id?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  requires_2fa?: boolean;
  is_profile_complete?: boolean;
  is_organization_complete?: boolean;
}

export interface UserProfileResponse {
  _id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  has_2fa_enabled: boolean;
  organization_id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  /** Access token stored in memory only to reduce XSS exposure */
  private accessToken: string | null = null;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    this.http.post<LoginResponse>(`${environment.apiUrl}/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(response => {
          this.accessToken = response.access_token;
        }),
        switchMap(() => this.fetchCurrentUser()),
        catchError(() => of(null))
      )
      .subscribe();
  }

  login(email: string, password: string): Observable<User> {
    return this.http.post<LoginResponse>(
      `${environment.apiUrl}/auth/login`,
      { email, password },
      { withCredentials: true }
    ).pipe(
      tap(response => {
        this.accessToken = response.access_token;
      }),
      switchMap(() => this.fetchCurrentUser()),
      catchError(error => {
        this.accessToken = null;
        throw error;
      })
    );
  }

  fetchCurrentUser(): Observable<User> {
    return this.http.get<UserProfileResponse>(`${environment.apiUrl}/users/me`).pipe(
      tap(profile => {
        const user: User = {
          id: profile._id,
          _id: profile._id,
          email: profile.email,
          first_name: profile.first_name,
          last_name: profile.last_name,
          name: profile.first_name && profile.last_name
            ? `${profile.first_name} ${profile.last_name}`
            : profile.email,
          role: profile.role,
          permissions: this.getPermissionsForRole(profile.role),
          is_active: profile.is_active,
          is_verified: profile.is_verified,
          has_2fa_enabled: profile.has_2fa_enabled,
          organization_id: profile.organization_id
        };
        this.currentUserSubject.next(user);
      }),
      switchMap(() => of(this.currentUserSubject.value!))
    );
  }

  private getPermissionsForRole(role: string): string[] {
    const rolePermissions: Record<string, string[]> = {
      'super_admin': ['*', 'manage_users', 'manage_organizations', 'view_analytics', 'manage_templates', 'manage_billing'],
      'admin': ['manage_users', 'view_analytics', 'manage_templates'],
      'owner': ['manage_users', 'view_analytics', 'manage_templates', 'manage_billing'],
      'member': ['view_analytics', 'use_templates'],
      'viewer': ['view_analytics']
    };
    return rolePermissions[role] || [];
  }

  logout(): void {
    this.http.post(`${environment.apiUrl}/auth/logout`, {}, { withCredentials: true }).subscribe({
      complete: () => {
        this.accessToken = null;
        this.currentUserSubject.next(null);
        this.router.navigate(['/super-admin/login']);
      },
      error: () => {
        this.accessToken = null;
        this.currentUserSubject.next(null);
        this.router.navigate(['/super-admin/login']);
      }
    });
  }

  refreshToken(): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(response => {
          this.accessToken = response.access_token;
          this.fetchCurrentUser().subscribe();
        })
      );
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  isAdmin(): boolean {
    const user = this.currentUserSubject.value;
    if (!user) return false;
    return environment.admin.allowedRoles.includes(user.role);
  }

  isSuperAdmin(): boolean {
    const user = this.currentUserSubject.value;
    if (!user) return false;
    return user.role === 'super_admin';
  }

  hasPermission(permission: string): boolean {
    const user = this.currentUserSubject.value;
    if (!user) return false;
    if (user.permissions.includes('*')) return true;
    return user.permissions.includes(permission);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }
}
